// src/store/thunks/karigar/orderKarigarThunks.js
// ============================================================================
// ORDER-KARIGAR INTEGRATION THUNKS
// ============================================================================
// These thunks connect to the backend routes at:
// /api/order-karigar/v1/...
//
// Backend Routes (from orderKarigarRoutes.js):
// POST   /available-karigars          - Get available karigars for order item
// GET    /karigar/:karigarId/queue    - Get karigar's job queue
// POST   /assign                      - Assign karigar to order item
// POST   /reassign                    - Reassign karigar for order item
// POST   /bulk-assign                 - Bulk assign karigars
// GET    /order/:orderId/status       - Get karigar status for order
// ============================================================================

import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../services/api";

// ============================================================================
// API INSTANCE
// ============================================================================

// ============================================================================
// AVAILABILITY THUNKS
// ============================================================================

/**
 * Fetch available karigars for an order item
 *
 * Backend: POST /v1/order-karigar/available-karigars
 *
 * This is the main endpoint for getting karigars when assigning to an order item.
 * It returns:
 * - productKarigars: Karigars linked to this specific product (priority)
 * - otherKarigars: Other capable karigars
 * - recommendations: Best overall, fastest, highest rated
 * - summary: Counts and availability info
 *
 * @param {Object} payload
 * @param {string} payload.productType - GOLD, SILVER, DIAMOND, STUDDED, PLATINUM
 * @param {string} payload.productSubType - RING, NECKLACE, BANGLE, etc.
 * @param {string} payload.branchCode - Primary branch code
 * @param {string[]} payload.availableBranches - Array of branch codes
 * @param {Object[]} payload.capableKarigars - Product's capable karigars array
 * @param {string} payload.workType - NEW_MAKING, REPAIR, etc.
 * @param {string[]} payload.requiredSkills - Required skills
 * @param {string} payload.priority - URGENT, HIGH, NORMAL, LOW
 * @param {string} payload.complexity - simple, medium, complex
 * @param {string} payload.targetDate - Expected completion date
 * @param {Object} payload.estimatedMakingTime - { simple, medium, complex, default }
 * @param {boolean} payload.includeOtherKarigars - Include karigars beyond product's list
 */
export const fetchAvailableKarigarsForOrderItem = createAsyncThunk(
  "orderKarigar/fetchAvailableKarigars",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/order-item-karigar/available-karigars",
        payload
      );
      return response.data.data;
    } catch (error) {
      console.error("[fetchAvailableKarigarsForOrderItem] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch available karigars"
      );
    }
  }
);

/**
 * Fetch available karigars with bulk availability format
 * Same as above but named differently for clarity in hook
 *
 * @param {Object} payload - Same as fetchAvailableKarigarsForOrderItem
 */
export const fetchBulkKarigarAvailability = createAsyncThunk(
  "orderKarigar/fetchBulkAvailability",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/order-item-karigar/available-karigars",
        {
          ...payload,
          includeOtherKarigars: payload.includeOtherKarigars ?? true,
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("[fetchBulkKarigarAvailability] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bulk availability"
      );
    }
  }
);

// ============================================================================
// ASSIGNMENT THUNKS
// ============================================================================

/**
 * Assign karigar to an order item
 *
 * Backend: POST /v1/order-karigar/assign
 *
 * Creates a job in karigar-service linked to the order item.
 *
 * @param {Object} payload
 * @param {string} payload.orderId - Order ID
 * @param {number} payload.orderItemIndex - Index of item in order.items array
 * @param {string} payload.karigarId - Karigar to assign
 * @param {string} payload.workType - NEW_MAKING, REPAIR, etc.
 * @param {string} payload.priority - URGENT, HIGH, NORMAL, LOW
 * @param {Object} payload.specifications - Job specifications
 * @param {Object} payload.materialRequired - Material requirements
 * @param {string} payload.expectedCompletionDate - Expected completion
 * @param {string} payload.notes - Admin notes
 * @param {Object} payload.adminInfo - { adminCode, adminName }
 * @param {Object} payload.product - Product details for job snapshot
 */
export const assignKarigarToOrderItem = createAsyncThunk(
  "orderKarigar/assignKarigar",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/order-item-karigar/assign", payload);
      return response.data.data;
    } catch (error) {
      console.error("[assignKarigarToOrderItem] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to assign karigar"
      );
    }
  }
);

/**
 * Reassign order item to different karigar
 *
 * Backend: POST /v1/order-karigar/reassign
 *
 * @param {Object} payload
 * @param {string} payload.orderId - Order ID
 * @param {number} payload.orderItemIndex - Index of item in order.items array
 * @param {string} payload.newKarigarId - New karigar to assign
 * @param {string} payload.reason - Reason for reassignment
 * @param {Object} payload.adminInfo - { adminCode, adminName }
 */
export const reassignKarigarForOrderItem = createAsyncThunk(
  "orderKarigar/reassignKarigar",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/order-item-karigar/reassign", payload);
      return response.data.data;
    } catch (error) {
      console.error("[reassignKarigarForOrderItem] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to reassign karigar"
      );
    }
  }
);

/**
 * Bulk assign karigars to multiple order items
 *
 * Backend: POST /v1/order-karigar/bulk-assign
 *
 * Auto-selects best available karigars for each item.
 *
 * @param {Object} payload
 * @param {string} payload.orderId - Order ID
 * @param {Object[]} payload.items - Array of items to assign
 * @param {number} payload.items[].orderItemIndex - Item index
 * @param {Object} payload.items[].product - Product details
 * @param {string} payload.items[].workType - Work type
 * @param {string} payload.items[].priority - Priority
 * @param {Object} payload.items[].specifications - Specifications
 * @param {Object} payload.adminInfo - { adminCode, adminName }
 */
export const bulkAssignKarigars = createAsyncThunk(
  "orderKarigar/bulkAssign",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/order-item-karigar/bulk-assign",
        payload
      );
      return response.data.data;
    } catch (error) {
      console.error("[bulkAssignKarigars] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to bulk assign karigars"
      );
    }
  }
);

// ============================================================================
// STATUS & TRACKING THUNKS
// ============================================================================

/**
 * Get karigar work status for an order
 *
 * Backend: GET /v1/order-karigar/order/:orderId/status
 *
 * Returns all jobs and their statuses for an order.
 *
 * @param {string} orderId - Order ID
 */
export const getOrderKarigarStatus = createAsyncThunk(
  "orderKarigar/getOrderStatus",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/order/${orderId}/status`);
      return response.data.data;
    } catch (error) {
      console.error("[getOrderKarigarStatus] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to get order karigar status"
      );
    }
  }
);

/**
 * Get karigar's current job queue
 *
 * Backend: GET /v1/order-karigar/karigar/:karigarId/queue
 *
 * @param {string} karigarId - Karigar ID
 * @param {Object} options - Query options
 * @param {boolean} options.includeCompleted - Include completed jobs
 */
export const getKarigarQueue = createAsyncThunk(
  "orderKarigar/getKarigarQueue",
  async ({ karigarId, includeCompleted = false }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin-karigar/${karigarId}/queue`, {
        params: { includeCompleted },
      });
      return response.data.data;
    } catch (error) {
      console.error("[getKarigarQueue] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to get karigar queue"
      );
    }
  }
);

// ============================================================================
// HELPER THUNKS
// ============================================================================

/**
 * Check if order can be fulfilled based on karigar work status
 *
 * @param {string} orderId - Order ID
 */
export const checkOrderCanFulfill = createAsyncThunk(
  "orderKarigar/checkCanFulfill",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/order/${orderId}/status`);
      const data = response.data.data;

      return {
        orderId,
        canFulfill: data.canFulfillOrder,
        summary: data.summary,
        allCompleted: data.summary?.allCompleted || false,
        pendingJobs: data.summary?.pendingJobs || 0,
        overdueJobs: data.summary?.overdueJobs || 0,
      };
    } catch (error) {
      console.error("[checkOrderCanFulfill] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to check if order can fulfill"
      );
    }
  }
);

/**
 * Assign karigar via order (convenience wrapper)
 *
 * @param {string} orderId - Order ID
 * @param {number} itemIndex - Item index
 * @param {string} karigarId - Karigar ID
 * @param {Object} workDetails - Work details
 */
export const assignKarigarViaOrder = createAsyncThunk(
  "orderKarigar/assignViaOrder",
  async (
    { orderId, itemIndex, karigarId, workDetails = {} },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const order = state.orders?.order;
      const item = order?.items?.[itemIndex];

      const payload = {
        orderId,
        orderItemIndex: itemIndex,
        karigarId,
        workType: workDetails.workType || "NEW_MAKING",
        priority: workDetails.priority || "NORMAL",
        expectedCompletionDate:
          workDetails.expectedCompletionDate || order?.expectedDeliveryDate,
        notes: workDetails.notes || "",
        specifications: workDetails.specifications || {},
        product: item
          ? {
              productId: item.product?._id || item.productSnapshot?._id,
              name: item.productSnapshot?.name || item.product?.name,
              sku: item.productSnapshot?.sku || item.product?.sku,
              type:
                item.productSnapshot?.productType || item.product?.productType,
              subType:
                item.productSnapshot?.productSubType ||
                item.product?.productSubType,
              netWeight:
                item.productSnapshot?.netWeight || item.product?.netWeight,
              metalDetails:
                item.productSnapshot?.metalDetails ||
                item.product?.metalDetails,
            }
          : {},
        adminInfo: workDetails.adminInfo || {},
      };

      const response = await api.post("/order-item-karigar/assign", payload);
      return response.data.data;
    } catch (error) {
      console.error("[assignKarigarViaOrder] Error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to assign karigar"
      );
    }
  }
);

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  fetchAvailableKarigarsForOrderItem,
  fetchBulkKarigarAvailability,
  assignKarigarToOrderItem,
  reassignKarigarForOrderItem,
  bulkAssignKarigars,
  getOrderKarigarStatus,
  getKarigarQueue,
  checkOrderCanFulfill,
  assignKarigarViaOrder,
};
